"use client";

import { useState } from "react";
import { X } from "lucide-react";
import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import { Helmet } from "react-helmet-async";
import "./Gallery.css";

// Sample data - replace with your actual data
const imageData = [
  {
    id: 1,
    src: "/placeholder.svg?height=400&width=600",
    alt: "Gallery image 1",
  },
  {
    id: 2,
    src: "/placeholder.svg?height=500&width=400",
    alt: "Gallery image 2",
  },
  {
    id: 3,
    src: "/placeholder.svg?height=600&width=600",
    alt: "Gallery image 3",
  },
  {
    id: 4,
    src: "/placeholder.svg?height=400&width=500",
    alt: "Gallery image 4",
  },
  {
    id: 5,
    src: "/placeholder.svg?height=500&width=600",
    alt: "Gallery image 5",
  },
  {
    id: 6,
    src: "/placeholder.svg?height=600&width=400",
    alt: "Gallery image 6",
  },
  {
    id: 7,
    src: "/placeholder.svg?height=400&width=400",
    alt: "Gallery image 7",
  },
  {
    id: 8,
    src: "/placeholder.svg?height=500&width=500",
    alt: "Gallery image 8",
  },
];

const videoData = [
  {
    id: 1,
    src: "https://example.com/video1.mp4",
    thumbnail: "/placeholder.svg?height=400&width=600",
    alt: "Video 1",
  },
  {
    id: 2,
    src: "https://example.com/video2.mp4",
    thumbnail: "/placeholder.svg?height=500&width=400",
    alt: "Video 2",
  },
  {
    id: 3,
    src: "https://example.com/video3.mp4",
    thumbnail: "/placeholder.svg?height=600&width=600",
    alt: "Video 3",
  },
  {
    id: 4,
    src: "https://example.com/video4.mp4",
    thumbnail: "/placeholder.svg?height=400&width=500",
    alt: "Video 4",
  },
];

export default function Gallery() {
  const [activeTab, setActiveTab] = useState("images");
  const [selectedItem, setSelectedItem] = useState(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedItem(null);
  };

  const openModal = (item) => {
    setSelectedItem(item);
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  return (
    <>
      <Helmet>
        <title>ISKCON Vijayawada Gallery – HD Photos & Devotional Videos</title>
        <meta
          name="description"
          content="Browse HD photos and videos of ISKCON Vijayawada’s temple events, festivals, kirtans, and daily deities for a visual dive into Krishna bhakti"
        />
        <meta
          name="keywords"
          content="ISKCON photo gallery, Krishna temple videos, HD devotional photos, ISKCON media Vijayawada"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="ISKCON Vijayawada" />
        <meta
          property="og:title"
          content="ISKCON Vijayawada Gallery – HD Photos & Devotional Videos"
        />
        <meta
          property="og:description"
          content="Browse HD photos and videos of ISKCON Vijayawada’s temple events, festivals, kirtans, and daily deities for a visual dive into Krishna bhakti"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.iskconvijayawada.org/HD-Photos-Videos"
        />
        <meta
          property="og:image"
          content="https://www.iskconvijayawada.org/static/media/logo.467e4780dde7b11ae4cf.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="ISKCON Vijayawada Gallery – HD Photos & Devotional Videos"
        />
        <meta
          name="twitter:description"
          content="Browse HD photos and videos of ISKCON Vijayawada’s temple events, festivals, kirtans, and daily deities for a visual dive into Krishna bhakti"
        />
        <meta
          name="twitter:image"
          content="https://www.iskconvijayawada.org/static/media/logo.467e4780dde7b11ae4cf.png"
        />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href="https://www.iskconvijayawada.org/HD-Photos-Videos"
        />
      </Helmet>

      <Header />
      <div className="Gallery-section-heading">
        <h1 className="Gallery-heading">
          Photo & Video Gallery – ISKCON Vijayawada Moments
        </h1>
        <h2 className="Gallery-subheading">
          Watch Festivals, Kirtans & Deity Darshans in HD Quality
        </h2>
      </div>
      <div className="container mx-auto px-4 py-8 mt-4">
        <h1 className="mb-8 text-center text-3xl font-bold text-gray-900">
          Media Gallery
        </h1>

        {/* Header with toggle */}
        <div className="mb-8">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => handleTabChange("images")}
              className={`inline-block rounded-md px-4 py-2 text-sm font-medium ${
                activeTab === "images"
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Images
            </button>
            <button
              onClick={() => handleTabChange("videos")}
              className={`inline-block rounded-md px-4 py-2 text-sm font-medium ${
                activeTab === "videos"
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Videos
            </button>
          </div>
        </div>

        {/* Gallery grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {activeTab === "images" &&
            imageData.map((image) => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-lg bg-white shadow-md transition-all hover:shadow-lg"
                onClick={() => openModal(image)}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={image.src || "/placeholder.svg"}
                    alt={image.alt}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 transition-all group-hover:bg-opacity-20">
                  <span className="scale-0 transform rounded-full bg-white p-2 text-gray-800 transition-all group-hover:scale-100">
                    View
                  </span>
                </div>
              </div>
            ))}

          {activeTab === "videos" &&
            videoData.map((video) => (
              <div
                key={video.id}
                className="group relative overflow-hidden rounded-lg bg-white shadow-md transition-all hover:shadow-lg"
                onClick={() => openModal(video)}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={video.thumbnail || "/placeholder.svg"}
                    alt={video.alt}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white bg-opacity-75 text-gray-800">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-6 w-6"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        {/* Modal for full-size view */}
        {selectedItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
            <div className="relative max-h-[90vh] max-w-[90vw] overflow-auto">
              <button
                onClick={closeModal}
                className="absolute right-2 top-2 z-10 rounded-full bg-white p-2 text-gray-800 shadow-md hover:bg-gray-100"
              >
                <X className="h-6 w-6" />
              </button>

              {activeTab === "images" ? (
                <img
                  src={selectedItem.src || "/placeholder.svg"}
                  alt={selectedItem.alt}
                  className="max-h-[85vh] rounded-lg object-contain"
                />
              ) : (
                <video
                  src={selectedItem.src}
                  poster={selectedItem.thumbnail}
                  controls
                  className="max-h-[85vh] rounded-lg"
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
